import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import type { PostgrestError } from '@supabase/supabase-js'
import { CalendarDays, Plus } from 'lucide-react'
import {
  ADMIN_BOOKING_SELECT,
  asAdminBookings,
  attentionItems,
  bookingListStatus,
  bookingPackage,
  bookingVehicleLabel,
  buildOverviewMetrics,
  formatAppointment,
  upcomingConfirmedAppointments,
  type AdminCanonicalBooking,
  type AdminCustomerRow,
  type AdminQuoteRow,
} from '../../lib/admin-workspace'
import { supabase } from '../../lib/supabase'
import { toAdminBookingSearch, type AdminBookingAction } from '../../lib/admin-booking-actions'
import { AdminBookingConfirmDialog, type AdminDestructiveAction } from '../admin/admin-booking-confirm-dialog'
import { AdminBookingMenu } from '../admin/admin-booking-menu'
import { AdminSidebar } from '../admin/admin-sidebar'
import { PageTitle } from '../seo/page-title'

export interface AdminDashboardProps {}

type LoadStatus = 'loading' | 'ready' | 'error'

function daysAgo(dateValue: string) {
  const diff = Date.now() - new Date(dateValue).getTime()
  const minutes = Math.max(1, Math.floor(diff / 60000))
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

function formatDateKey(date = new Date()) {
  return date.toISOString().slice(0, 10)
}

export function AdminDashboard(_: Readonly<AdminDashboardProps>) {
  const navigate = useNavigate()
  const [bookings, setBookings] = useState<AdminCanonicalBooking[]>([])
  const [customers, setCustomers] = useState<AdminCustomerRow[]>([])
  const [quotes, setQuotes] = useState<AdminQuoteRow[]>([])
  const [status, setStatus] = useState<LoadStatus>('loading')
  const [message, setMessage] = useState<string | null>(null)
  const [pendingAction, setPendingAction] = useState<AdminDestructiveAction | null>(null)
  const [confirmBooking, setConfirmBooking] = useState<AdminCanonicalBooking | null>(null)
  const [noticeError, setNoticeError] = useState(false)

  const loadDashboard = useCallback(async (silent = false) => {
    if (!silent) setStatus('loading')

    try {
      const [bookingResult, customerResult, quoteResult] = await Promise.all([
        supabase
          .from('bookings')
          .select(ADMIN_BOOKING_SELECT)
          .order('created_at', { ascending: false })
          .limit(300),
        supabase
          .from('customers')
          .select('id, created_at, full_name, first_name, last_name, email, phone, source, last_booking_at, total_bookings')
          .limit(300),
        supabase
          .from('quote_requests')
          .select('id, public_reference, created_at, status, customer_name, customer_email')
          .order('created_at', { ascending: false })
          .limit(100),
      ])

      if (bookingResult.error) throw bookingResult.error
      if (customerResult.error) throw customerResult.error
      if (quoteResult.error) throw quoteResult.error

      setBookings(asAdminBookings(bookingResult.data))
      setCustomers((customerResult.data ?? []) as AdminCustomerRow[])
      setQuotes((quoteResult.data ?? []) as AdminQuoteRow[])
      setStatus('ready')
    } catch (error) {
      const adminError = error as PostgrestError
      setMessage(adminError.message || 'Unable to load dashboard data from Supabase.')
      setStatus('error')
    }
  }, [])

  useEffect(() => {
    void loadDashboard()
  }, [loadDashboard])

  const metrics = useMemo(
    () => buildOverviewMetrics({ bookings, customers, quotes }),
    [bookings, customers, quotes],
  )
  const upcomingBookings = useMemo(() => upcomingConfirmedAppointments(bookings).slice(0, 6), [bookings])
  const attention = useMemo(() => attentionItems({ bookings, quotes }), [bookings, quotes])
  const activity = useMemo(() => bookings.slice(0, 4), [bookings])
  const density = useMemo(() => {
    const counts = new Map<string, number>()
    bookings.forEach((booking) => {
      if (!booking.start_at) return
      counts.set(booking.start_at.slice(0, 10), (counts.get(booking.start_at.slice(0, 10)) ?? 0) + 1)
    })
    return Array.from({ length: 28 }, (_, index) => {
      const date = new Date()
      date.setDate(date.getDate() + index)
      const count = counts.get(formatDateKey(date)) ?? 0
      return count > 2 ? 'hot' : count > 0 ? 'warm' : ''
    })
  }, [bookings])

  function viewBooking(booking: AdminCanonicalBooking) {
    void navigate({
      to: '/admin/bookings',
      search: toAdminBookingSearch(booking.public_reference, 'view'),
    })
  }

  function editBooking(booking: AdminCanonicalBooking) {
    void navigate({
      to: '/admin/bookings',
      search: toAdminBookingSearch(booking.public_reference, 'edit'),
    })
  }

  function cancelBooking(booking: AdminCanonicalBooking) {
    setConfirmBooking(booking)
    setPendingAction('cancel')
  }

  function handleMenuAction(booking: AdminCanonicalBooking, menuAction: AdminBookingAction) {
    if (menuAction === 'edit') editBooking(booking)
    else if (menuAction === 'cancel') cancelBooking(booking)
    else viewBooking(booking)
  }

  return (
    <div className="admin-dashboard">
      <PageTitle title="Admin Dashboard | AUTOGLOW Detailing" />
      <AdminSidebar active="overview" />
      <main>
        <header className="admin-topbar">
          <div>
            <p className="eyebrow">Command Center</p>
            <h1>Studio Overview</h1>
          </div>
          <a href="/book"><Plus size={18} /> New Booking</a>
        </header>

        {message ? <p className={noticeError || status === 'error' ? 'admin-alert error' : 'admin-alert'}>{message}</p> : null}

        <section className="metric-grid">
          {metrics.map((metric) => (
            <article key={metric.label}>
              <span>{metric.label}</span>
              <strong>{metric.value}</strong>
              <p>{metric.note}</p>
            </article>
          ))}
        </section>

        <section className="admin-content-grid">
          <article className="admin-card appointments-card">
            <div className="admin-card-heading">
              <h2>Today / Upcoming</h2>
              <a href="/admin/bookings">VIEW ALL SCHEDULE</a>
            </div>
            {status === 'loading' ? <p className="admin-empty-state">Loading bookings from Supabase...</p> : null}
            {status !== 'loading' && upcomingBookings.length === 0 ? (
              <p className="admin-empty-state">No upcoming confirmed appointments.</p>
            ) : null}
            {upcomingBookings.length > 0 ? (
              <table>
                <tbody>
                  {upcomingBookings.map((booking) => (
                    <tr
                      className="admin-clickable-row"
                      key={booking.id}
                      onClick={() => viewBooking(booking)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          viewBooking(booking)
                        }
                      }}
                      tabIndex={0}
                    >
                      <td>{formatAppointment(booking.start_at, booking.appointment_timezone ?? undefined)}</td>
                      <td>
                        <strong>{booking.customer_name || booking.name || 'Customer'}</strong>
                        <span>{bookingVehicleLabel(booking)} · {bookingPackage(booking).displayName}</span>
                      </td>
                      <td><span className="status-pill">{bookingListStatus(booking)}</span></td>
                      <td onClick={(event) => event.stopPropagation()}>
                        <AdminBookingMenu
                          booking={booking}
                          onAction={handleMenuAction}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
          </article>

          <aside className="admin-card">
            <h2>Needs Attention</h2>
            {attention.length === 0 ? <p className="admin-empty-state">Nothing waiting right now.</p> : null}
            {attention.map((item) => (
              <a className="admin-attention-item" href={item.href} key={item.id}>
                <p><strong>{item.title}</strong> {item.detail}</p>
              </a>
            ))}
          </aside>

          <aside className="admin-card">
            <h2><CalendarDays size={18} /> Booking Density</h2>
            <div className="density-grid">
              {density.map((densityClass, index) => (
                <span className={densityClass} key={index} />
              ))}
            </div>
          </aside>

          <aside className="admin-card activity-card">
            <h2>Recent Activity</h2>
            {activity.length === 0 ? <p className="admin-empty-state">No booking activity yet.</p> : null}
            {activity.map((booking) => (
              <button
                className="admin-activity-item"
                key={booking.id}
                onClick={() => viewBooking(booking)}
                type="button"
              >
                <p><strong>{bookingListStatus(booking)}</strong> {booking.public_reference} - {bookingPackage(booking).displayName}</p>
                <span>{daysAgo(booking.created_at)}</span>
              </button>
            ))}
          </aside>
        </section>
      </main>
      <AdminBookingConfirmDialog
        action={pendingAction}
        booking={confirmBooking}
        onCancelled={() => { void loadDashboard(true) }}
        onDismiss={() => {
          setPendingAction(null)
          setConfirmBooking(null)
        }}
        onMessage={(next, error) => {
          setMessage(next)
          setNoticeError(Boolean(error))
        }}
      />
    </div>
  )
}

export default AdminDashboard
